/**
 * Export dialog — format (CSV / Excel) + header strategy (label vs field_key) +
 * form-prefix toggle (§15.6). Exports ALL matching rows.
 */
import { useState } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils/cn'
import { FileText, FileSpreadsheet } from 'lucide-react'
import type { HeaderStrategy } from '../lib/export'

export interface ExportOptions {
  format: 'csv' | 'xlsx'
  headerStrategy: HeaderStrategy
  formPrefix: boolean
}

interface ExportDialogProps {
  open: boolean
  onOpenChange: (o: boolean) => void
  total: number
  exporting: boolean
  onExport: (opts: ExportOptions) => void
}

export function ExportDialog({ open, onOpenChange, total, exporting, onExport }: ExportDialogProps) {
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx')
  const [headerStrategy, setHeaderStrategy] = useState<HeaderStrategy>('label')
  const [formPrefix, setFormPrefix] = useState(false)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>خروجی داده</DialogTitle>
          <DialogDescription>{total} ردیف با پیکربندی فعلی ستون‌ها و فیلترها خروجی می‌گیرد.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FmtCard active={format === 'xlsx'} onClick={() => setFormat('xlsx')} icon={FileSpreadsheet} title="Excel (.xlsx)" desc="چندبرگی + متادیتا" />
            <FmtCard active={format === 'csv'} onClick={() => setFormat('csv')} icon={FileText} title="CSV" desc="UTF-8 با BOM" />
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">عنوان ستون‌ها</p>
            <div className="flex gap-2">
              <Toggle active={headerStrategy === 'label'} onClick={() => setHeaderStrategy('label')} label="برچسب فارسی" />
              <Toggle active={headerStrategy === 'field_key'} onClick={() => setHeaderStrategy('field_key')} label="کلید فیلد" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={formPrefix} onCheckedChange={(v) => setFormPrefix(!!v)} />
            افزودن پیشوند نام فرم به ستون‌ها
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>انصراف</Button>
          <Button disabled={exporting} onClick={() => onExport({ format, headerStrategy, formPrefix })}>
            {exporting ? 'در حال آماده‌سازی…' : 'دریافت خروجی'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FmtCard({ active, onClick, icon: Icon, title, desc }: { active: boolean; onClick: () => void; icon: typeof FileText; title: string; desc: string }) {
  return (
    <button onClick={onClick} type="button"
      className={cn('flex flex-col items-start gap-0.5 rounded-md border p-2.5 text-start transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted')}>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Icon className="w-3.5 h-3.5" />{title}</span>
      <span className="text-[11px] text-muted-foreground">{desc}</span>
    </button>
  )
}

function Toggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} type="button"
      className={cn('px-2.5 py-1.5 rounded-md text-sm border transition-colors',
        active ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted')}>
      {label}
    </button>
  )
}
